import { useContext, useEffect, useRef, useState, type ChangeEvent } from "react";
import {
  ArrowLeft,
  Headset,
  ImagePlus,
  Plus,
  SendHorizontal,
  Star,
  X,
} from "lucide-react";
import { UsuarioContext } from "@/contexts/UsuarioContext";
import chamados_api from "@/services/chamados_api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// Identificador desta aplicação na API de chamados.
const APP_ID = "impressaoweb";

type Categoria = {
  id: string;
  label: string;
  subcategorias: string[];
};

type Chamado = {
  _id: string;
  cod: string;
  categoria: string;
  subcategoria?: string;
  descricao?: string;
  status:
    | "aberto"
    | "em_atendimento"
    | "aguardando_dev"
    | "resolvido"
    | "encerrado";
  canalResposta: string;
  lastMessage?: string;
  lastMessageAt?: string;
  naoLidasCliente: number;
  createdAt: string;
  avaliacao?: { data?: string };
};

type Mensagem = {
  _id: string;
  autor: "cliente" | "suporte";
  autorNome?: string;
  texto: string;
  tipo?: string;
  imagemUrl?: string;
  createdAt: string;
};

const statusMeta: Record<string, { label: string; classes: string }> = {
  aberto: { label: "Aberto", classes: "bg-amber-500/15 text-amber-600" },
  em_atendimento: {
    label: "Em atendimento",
    classes: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  },
  aguardando_dev: {
    label: "Aguardando dev",
    classes: "bg-violet-500/15 text-violet-600 dark:text-violet-400",
  },
  resolvido: { label: "Resolvido", classes: "bg-green-500/15 text-green-600" },
  encerrado: {
    label: "Encerrado",
    classes: "bg-muted text-muted-foreground",
  },
};

// Contexto técnico coletado automaticamente (equivalente web de versão/dispositivo).
function montarContexto() {
  const ua = navigator.userAgent;
  const browser = ua.includes("Edg")
    ? "Edge"
    : ua.includes("Chrome")
      ? "Chrome"
      : ua.includes("Firefox")
        ? "Firefox"
        : ua.includes("Safari")
          ? "Safari"
          : "Navegador";
  const os = ua.includes("Windows")
    ? "Windows"
    : ua.includes("Mac")
      ? "macOS"
      : ua.includes("Linux")
        ? "Linux"
        : ua.includes("Android")
          ? "Android"
          : "";
  return {
    plataforma: `${browser}${os ? ` · ${os}` : ""}`,
    dispositivo: `Web · ${window.screen.width}x${window.screen.height}`,
  };
}

function formatarData(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  return (
    d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) +
    " " +
    d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  );
}

function StatusChip({ status }: { status: string }) {
  const meta = statusMeta[status] ?? {
    label: status,
    classes: "bg-muted text-muted-foreground",
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${meta.classes}`}
    >
      {meta.label}
    </span>
  );
}

const selectClasses =
  "h-8 w-full rounded-md border border-input bg-transparent px-2 text-xs shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-ring dark:bg-input/30";

// Linha de 0 a 10 para a avaliação pós-atendimento.
function NotaRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <span className="text-[11px] font-medium text-muted-foreground">
        {label}
      </span>
      <div className="mt-0.5 flex gap-0.5">
        {Array.from({ length: 11 }, (_, n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={`h-6 flex-1 rounded text-[10px] font-semibold transition ${
              value === n
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChamadosWidget() {
  const ctx = useContext(UsuarioContext);
  const usuario = ctx?.usuario;

  const [disponivel, setDisponivel] = useState(false);
  const [aberto, setAberto] = useState(false);
  const [view, setView] = useState<"lista" | "novo" | "thread">("lista");

  const [chamados, setChamados] = useState<Chamado[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [selecionado, setSelecionado] = useState<Chamado | null>(null);

  // Form de novo chamado
  const [categoria, setCategoria] = useState("");
  const [subcategoria, setSubcategoria] = useState("");
  const [descricao, setDescricao] = useState("");
  const [canalResposta, setCanalResposta] = useState("chat");
  const [contatoResposta, setContatoResposta] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState("");

  // Thread
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [texto, setTexto] = useState("");
  const [enviandoMsg, setEnviandoMsg] = useState(false);
  const lastAtRef = useRef<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Avaliação
  const [notaAtendimento, setNotaAtendimento] = useState<number | null>(null);
  const [notaResolucao, setNotaResolucao] = useState<number | null>(null);
  const [notaRecomendacao, setNotaRecomendacao] = useState<number | null>(null);
  const [comentario, setComentario] = useState("");

  const idUsuario = usuario?._id;

  // Probe: só mostra o botão se a API de chamados estiver acessível.
  useEffect(() => {
    if (!idUsuario) return;
    chamados_api
      .get("/chamados/health")
      .then(() => setDisponivel(true))
      .catch(() => setDisponivel(false));
  }, [idUsuario]);

  // Lista "meus chamados" — alimenta a lista e o badge de não lidas.
  useEffect(() => {
    if (!disponivel || !idUsuario) return;

    let active = true;
    function load() {
      chamados_api
        .get(`/chamados/cliente/${idUsuario}`, { params: { app: APP_ID } })
        .then((res) => {
          if (active) setChamados(res.data);
        })
        .catch(() => {});
    }

    load();
    const timer = setInterval(load, aberto ? 5000 : 15000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [disponivel, idUsuario, aberto]);

  // Categorias para o form (carrega quando abre o painel).
  useEffect(() => {
    if (!aberto || categorias.length) return;
    chamados_api
      .get("/chamados/categorias")
      .then((res) => setCategorias(res.data))
      .catch(() => {});
  }, [aberto, categorias.length]);

  // Polling das mensagens do chamado aberto.
  useEffect(() => {
    if (view !== "thread" || !selecionado) return;

    let active = true;
    const id = selecionado._id;
    lastAtRef.current = null;
    setMensagens([]);

    const scroll = () =>
      setTimeout(
        () => bottomRef.current?.scrollIntoView({ behavior: "smooth" }),
        50,
      );

    chamados_api.get(`/chamados/${id}/mensagens`).then((res) => {
      if (!active) return;
      setMensagens(res.data);
      if (res.data.length)
        lastAtRef.current = res.data[res.data.length - 1].createdAt;
      scroll();
    });
    chamados_api.post(`/chamados/${id}/read`, { leitor: "cliente" }).catch(() => {});

    const timer = setInterval(() => {
      const params = lastAtRef.current ? { after: lastAtRef.current } : {};
      chamados_api.get(`/chamados/${id}/mensagens`, { params }).then((res) => {
        if (!active || !res.data.length) return;
        setMensagens((prev) => [...prev, ...res.data]);
        lastAtRef.current = res.data[res.data.length - 1].createdAt;
        chamados_api
          .post(`/chamados/${id}/read`, { leitor: "cliente" })
          .catch(() => {});
        scroll();
      });
      // Mantém o status atualizado (resolvido -> mostra avaliação).
      chamados_api.get(`/chamados/${id}`).then((res) => {
        if (active) setSelecionado(res.data);
      });
    }, 2000);

    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [view, selecionado?._id]);

  if (!idUsuario || !disponivel) return null;

  const naoLidas = chamados.reduce(
    (acc, c) => acc + (c.naoLidasCliente || 0),
    0,
  );
  const catSelecionada = categorias.find((c) => c.id === categoria);

  function abrirNovo() {
    setCategoria("");
    setSubcategoria("");
    setDescricao("");
    setCanalResposta("chat");
    setContatoResposta("");
    setErro("");
    setView("novo");
  }

  function criarChamado() {
    if (!categoria) {
      setErro("Selecione a categoria do problema.");
      return;
    }
    if (canalResposta !== "chat" && !contatoResposta.trim()) {
      setErro(`Informe o ${canalResposta} para contato.`);
      return;
    }
    setEnviando(true);
    setErro("");
    chamados_api
      .post("/chamados", {
        cliente: {
          id_usuario: usuario?._id,
          id_conta: usuario?.id_conta,
          nome: usuario?.nome,
          login: usuario?.login,
        },
        app: APP_ID,
        contexto: montarContexto(),
        categoria,
        subcategoria,
        descricao,
        canalResposta,
        contatoResposta,
      })
      .then((res) => {
        setChamados((prev) => [res.data, ...prev]);
        setSelecionado(res.data);
        setView("thread");
      })
      .catch((e) =>
        setErro(e?.response?.data?.message || "Falha ao abrir o chamado."),
      )
      .finally(() => setEnviando(false));
  }

  // Envio unificado: aceita corpo JSON (texto) ou FormData (imagem com legenda).
  function postMensagem(body: object | FormData) {
    if (!selecionado) return;
    setEnviandoMsg(true);
    chamados_api
      .post(`/chamados/${selecionado._id}/mensagens`, body)
      .then((res) => {
        setMensagens((prev) => [...prev, res.data]);
        lastAtRef.current = res.data.createdAt;
        setTexto("");
        setTimeout(
          () => bottomRef.current?.scrollIntoView({ behavior: "smooth" }),
          50,
        );
      })
      .catch((e) =>
        setErro(e?.response?.data?.message || "Falha ao enviar mensagem."),
      )
      .finally(() => setEnviandoMsg(false));
  }

  function enviarMensagem() {
    const value = texto.trim();
    if (!value || !selecionado || enviandoMsg) return;
    postMensagem({
      autor: "cliente",
      autorNome: usuario?.nome,
      autorId: usuario?._id,
      texto: value,
    });
  }

  // Anexa uma imagem (multipart) com legenda opcional — mesmo fluxo do suporte.
  function handlePickImage(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite reenviar o mesmo arquivo
    if (!file || !selecionado || enviandoMsg) return;

    const form = new FormData();
    form.append("autor", "cliente");
    if (usuario?.nome) form.append("autorNome", usuario.nome);
    if (usuario?._id) form.append("autorId", usuario._id);
    if (texto.trim()) form.append("texto", texto.trim());
    form.append("imagem", file);

    postMensagem(form);
  }

  function enviarAvaliacao() {
    if (!selecionado) return;
    if (
      notaAtendimento === null ||
      notaResolucao === null ||
      notaRecomendacao === null
    ) {
      setErro("Preencha as três notas (0 a 10).");
      return;
    }
    setEnviando(true);
    setErro("");
    chamados_api
      .post(`/chamados/${selecionado._id}/avaliacao`, {
        notaAtendimento,
        notaResolucao,
        notaRecomendacao,
        comentario,
      })
      .then((res) => {
        setSelecionado(res.data);
        setNotaAtendimento(null);
        setNotaResolucao(null);
        setNotaRecomendacao(null);
        setComentario("");
      })
      .catch((e) =>
        setErro(e?.response?.data?.message || "Falha ao enviar avaliação."),
      )
      .finally(() => setEnviando(false));
  }

  function abrirThread(chamado: Chamado) {
    setSelecionado(chamado);
    setErro("");
    setView("thread");
  }

  const mostrarAvaliacao =
    selecionado &&
    ["resolvido", "encerrado"].includes(selecionado.status) &&
    !selecionado.avaliacao?.data;

  const encerrado = selecionado?.status === "encerrado";

  return (
    <>
      {/* Botão flutuante */}
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-label="Suporte"
        className="fixed bottom-4 right-4 z-50 grid h-12 w-12 place-items-center rounded-full bg-primary text-primary-foreground shadow-lg transition hover:scale-105"
      >
        {aberto ? <X size={20} /> : <Headset size={20} />}
        {!aberto && naoLidas > 0 && (
          <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white">
            {naoLidas}
          </span>
        )}
      </button>

      {/* Painel */}
      {aberto && (
        <div className="fixed bottom-20 right-4 z-50 flex max-h-[70vh] w-[360px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-2xl">
          {/* Cabeçalho */}
          <div className="flex items-center gap-2 border-b bg-primary px-3 py-2.5 text-primary-foreground">
            {view !== "lista" && (
              <button
                type="button"
                onClick={() => {
                  setView("lista");
                  setErro("");
                }}
                className="rounded p-1 hover:bg-white/15"
                aria-label="Voltar"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <Headset size={18} />
            <div className="flex-1 leading-tight">
              <p className="text-sm font-bold">
                {view === "novo"
                  ? "Novo chamado"
                  : view === "thread"
                    ? `Chamado #${selecionado?.cod}`
                    : "Suporte iNOVACODE"}
              </p>
              {view === "lista" && (
                <p className="text-[10px] opacity-80">
                  Abra um chamado e acompanhe por aqui
                </p>
              )}
            </div>
            {view === "thread" && selecionado && (
              <StatusChip status={selecionado.status} />
            )}
          </div>

          {erro && (
            <p className="border-b border-destructive/20 bg-destructive/10 px-3 py-1.5 text-[11px] font-medium text-destructive">
              {erro}
            </p>
          )}

          {/* Lista de chamados */}
          {view === "lista" && (
            <>
              <div className="flex-1 overflow-y-auto">
                {chamados.length === 0 && (
                  <p className="px-4 py-8 text-center text-xs text-muted-foreground">
                    Você ainda não abriu nenhum chamado.
                  </p>
                )}
                {chamados.map((c) => (
                  <button
                    key={c._id}
                    type="button"
                    onClick={() => abrirThread(c)}
                    className="flex w-full flex-col gap-0.5 border-b px-3 py-2.5 text-left transition hover:bg-accent"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-xs font-bold">#{c.cod}</span>
                      <StatusChip status={c.status} />
                      <span className="flex-1" />
                      {c.naoLidasCliente > 0 && (
                        <span className="grid h-4 min-w-4 place-items-center rounded-full bg-destructive px-1 text-[9px] font-bold text-white">
                          {c.naoLidasCliente}
                        </span>
                      )}
                      <span className="text-[10px] text-muted-foreground">
                        {formatarData(c.createdAt)}
                      </span>
                    </span>
                    <span className="truncate text-xs font-medium">
                      {c.subcategoria || c.categoria}
                    </span>
                    {c.lastMessage && (
                      <span className="truncate text-[11px] text-muted-foreground">
                        {c.lastMessage}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <div className="border-t p-2.5">
                <Button size="sm" className="w-full" onClick={abrirNovo}>
                  <Plus size={15} />
                  Abrir chamado
                </Button>
              </div>
            </>
          )}

          {/* Novo chamado */}
          {view === "novo" && (
            <div className="flex flex-col gap-2.5 overflow-y-auto p-3">
              <label className="flex flex-col gap-1 text-[11px] font-medium text-muted-foreground">
                Qual é o problema?
                <select
                  value={categoria}
                  onChange={(e) => {
                    setCategoria(e.target.value);
                    setSubcategoria("");
                  }}
                  className={selectClasses}
                >
                  <option value="">Selecione...</option>
                  {categorias.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>

              {catSelecionada && (
                <label className="flex flex-col gap-1 text-[11px] font-medium text-muted-foreground">
                  Detalhe um pouco mais
                  <select
                    value={subcategoria}
                    onChange={(e) => setSubcategoria(e.target.value)}
                    className={selectClasses}
                  >
                    <option value="">Selecione...</option>
                    {catSelecionada.subcategorias.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className="flex flex-col gap-1 text-[11px] font-medium text-muted-foreground">
                Descreva o problema (opcional)
                <Textarea
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  rows={3}
                  placeholder="Conte o que está acontecendo..."
                  className="resize-none text-xs"
                />
              </label>

              <label className="flex flex-col gap-1 text-[11px] font-medium text-muted-foreground">
                Como prefere ser respondido?
                <select
                  value={canalResposta}
                  onChange={(e) => setCanalResposta(e.target.value)}
                  className={selectClasses}
                >
                  <option value="chat">Pelo chat do chamado</option>
                  <option value="telefone">Por telefone</option>
                  <option value="email">Por e-mail</option>
                </select>
              </label>

              {canalResposta !== "chat" && (
                <Input
                  value={contatoResposta}
                  onChange={(e) => setContatoResposta(e.target.value)}
                  placeholder={
                    canalResposta === "telefone"
                      ? "Telefone com DDD"
                      : "Seu e-mail"
                  }
                  className="h-8 text-xs"
                />
              )}

              <p className="text-[10px] text-muted-foreground">
                Conta, usuário e versão são enviados automaticamente — você não
                precisa informar.
              </p>

              <Button size="sm" onClick={criarChamado} disabled={enviando}>
                {enviando ? "Enviando..." : "Abrir chamado"}
              </Button>
            </div>
          )}

          {/* Conversa do chamado */}
          {view === "thread" && selecionado && (
            <>
              <div className="flex-1 space-y-1.5 overflow-y-auto bg-muted/40 p-2.5">
                {mensagens.map((m) => {
                  const doCliente = m.autor === "cliente";
                  return (
                    <div
                      key={m._id}
                      className={`flex ${doCliente ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-xl px-2.5 py-1.5 text-xs shadow-sm ${
                          doCliente
                            ? "rounded-br-sm bg-primary text-primary-foreground"
                            : "rounded-bl-sm bg-background"
                        }`}
                      >
                        {!doCliente && m.autorNome && (
                          <p className="text-[10px] font-bold text-blue-600 dark:text-blue-400">
                            {m.autorNome}
                          </p>
                        )}
                        {m.imagemUrl && (
                          <a
                            href={m.imagemUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-0.5 block"
                          >
                            <img
                              src={m.imagemUrl}
                              alt={m.texto || "imagem"}
                              className="max-h-64 max-w-full cursor-pointer rounded-lg"
                            />
                          </a>
                        )}
                        {m.texto && (
                          <p className="whitespace-pre-wrap break-words">
                            {m.texto}
                          </p>
                        )}
                        <p
                          className={`mt-0.5 text-right text-[9px] ${
                            doCliente
                              ? "text-primary-foreground/70"
                              : "text-muted-foreground"
                          }`}
                        >
                          {formatarData(m.createdAt)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Avaliação pós-resolução */}
              {mostrarAvaliacao && (
                <div className="space-y-2 border-t p-2.5">
                  <p className="flex items-center gap-1 text-xs font-bold">
                    <Star size={14} className="text-amber-500" />
                    Avalie o atendimento
                  </p>
                  <NotaRow
                    label="Como foi o atendimento?"
                    value={notaAtendimento}
                    onChange={setNotaAtendimento}
                  />
                  <NotaRow
                    label="O problema foi resolvido?"
                    value={notaResolucao}
                    onChange={setNotaResolucao}
                  />
                  <NotaRow
                    label="Recomendaria nosso suporte?"
                    value={notaRecomendacao}
                    onChange={setNotaRecomendacao}
                  />
                  <Input
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    placeholder="Comentário (opcional)"
                    className="h-8 text-xs"
                  />
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={enviarAvaliacao}
                    disabled={enviando}
                  >
                    Enviar avaliação
                  </Button>
                </div>
              )}

              {/* Caixa de envio */}
              {!mostrarAvaliacao && (
                <div className="flex items-center gap-1.5 border-t p-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handlePickImage}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={encerrado || enviandoMsg}
                    aria-label="Anexar imagem"
                  >
                    <ImagePlus size={17} />
                  </Button>
                  <Input
                    value={texto}
                    disabled={encerrado}
                    onChange={(e) => setTexto(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") enviarMensagem();
                    }}
                    placeholder={
                      encerrado ? "Chamado encerrado" : "Digite uma mensagem..."
                    }
                    className="h-8 flex-1 text-xs"
                  />
                  <Button
                    size="icon"
                    className="h-8 w-8"
                    onClick={enviarMensagem}
                    disabled={encerrado || enviandoMsg || !texto.trim()}
                    aria-label="Enviar"
                  >
                    <SendHorizontal size={15} />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

export default ChamadosWidget;
